export type TimePeriod = "Morning" | "Afternoon" | "Evening";

export type TimeSlotGroup = {
  period: TimePeriod;
  slots: number[];
};

const MINUTES_PER_DAY = 24 * 60;

/** Format minutes from midnight as "HH:MM" (24-hour) for API payloads */
export function minutesToApiTime(totalMinutes: number): string {
  const hours24 = Math.floor(totalMinutes / 60) % 24;
  const minutes = totalMinutes % 60;
  return `${hours24.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;
}

/** Format minutes from midnight as "10:00 AM" */
export function formatTimeLabel(totalMinutes: number): string {
  const hours24 = Math.floor(totalMinutes / 60) % 24;
  const minutes = totalMinutes % 60;
  const period = hours24 >= 12 ? "PM" : "AM";
  const hours12 = hours24 % 12 || 12;
  return `${hours12}:${minutes.toString().padStart(2, "0")} ${period}`;
}

export function getTimePeriod(totalMinutes: number): TimePeriod {
  const hours = Math.floor(totalMinutes / 60);
  if (hours < 12) {
    return "Morning";
  }
  if (hours < 17 || (hours === 17 && totalMinutes % 60 === 0)) {
    return "Afternoon";
  }
  return "Evening";
}

/** Build 30-minute slots between start and end (inclusive), in minutes from midnight */
export function generateTimeSlots(
  startMinutes = 0,
  endMinutes = MINUTES_PER_DAY - 30,
  intervalMinutes = 30,
): number[] {
  const slots: number[] = [];
  for (let m = startMinutes; m <= endMinutes; m += intervalMinutes) {
    slots.push(m);
  }
  return slots;
}

export function groupTimeSlots(slots: number[]): TimeSlotGroup[] {
  const groups = new Map<TimePeriod, number[]>();

  for (const slot of slots) {
    const period = getTimePeriod(slot);
    const existing = groups.get(period) ?? [];
    existing.push(slot);
    groups.set(period, existing);
  }

  const order: TimePeriod[] = ["Morning", "Afternoon", "Evening"];
  return order
    .filter((period) => groups.has(period))
    .map((period) => ({
      period,
      slots: groups.get(period)!,
    }));
}
